import { useEffect, useState } from 'react';
import { AppRoutes } from './AppRoutes';
import moment from 'moment';
import 'moment/dist/locale/nb';
import BaseIdbRepository from 'repositories/idb/BaseIdbRepository';
import AuthApiRepository from 'repositories/api/AuthApiRepository';
import SourceApiRepository from 'repositories/api/SourceApiRepository';
import { AppInsightsService } from './services/AppInsightsService';
import { isNullOrEmpty, isObjectNull } from './components/helpers/ObjectHelpers';
import { getToken } from './components/helpers/TokenHelpers';
import { getLoggedInPersonId, isLoggedIn, setWhoAmIAsync } from './components/helpers/AuthHelpers';
import { initCacheAsync } from './components/helpers/CacheHelpers';
import { RestoreDatabase } from './components/access/RestoreDatabase';
import { Loader } from '@progress/kendo-react-indicators';
import { Repository as SettingsIdbRepository } from "@pilot-web/settings-preferences";
import { createDateStyles, createTextStyles, PreferenceTypes } from "@pilot-web/settings-shared";
import { ApplicationRightService } from './services/ApplicationRightService';
import { ApplicationRightSystemNames } from './components/constants/ApplicationRightSystemNames';
import {
    useDarkmode,
    useSetDarkmode,
    useConfiguration,
    useSetConfiguration,
    useSetApplicationRights,
    useWarningNotification
} from './components/hooks/useAppStore';
import { useTokenParameter } from './components/hooks/useTokenParameter';

moment.locale('nb');

BaseIdbRepository.init();

const AppStates = {
    Processing: 'Processing',
    RestoreDatabase: 'RestoreDatabase',
    Valid: 'Valid',
}

export default function App() {
    const [appState, setAppState] = useState(AppStates.Processing);

    const darkmode = useDarkmode();
    const setDarkmode = useSetDarkmode();
    const configuration = useConfiguration();
    const setConfiguration = useSetConfiguration();
    const setApplicationRights = useSetApplicationRights();
    const addWarningNotification = useWarningNotification();
    
    const tokenParameter = useTokenParameter();

    useEffect(() => {

        setAppState(AppStates.Processing);

        const initializeTheme = () => {
            let darkmode = false;
            let theme = localStorage.getItem("theme");
            if (!isNullOrEmpty(theme)) {
                document.documentElement.classList = theme;
                darkmode = theme === "theme-dark";
            } else {
                if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                    document.documentElement.classList = "theme-dark";
                    localStorage.setItem("theme", "theme-dark");
                    darkmode = true;
                } else {
                    document.documentElement.classList = "theme-light";
                    localStorage.setItem("theme", "theme-light");
                }
            }
            setDarkmode(darkmode);
        }

        const initializeStyle = async () => {
            const textSize = await SettingsIdbRepository.getFontStyle(PreferenceTypes.TextSize);
            const dateSize = await SettingsIdbRepository.getFontStyle(PreferenceTypes.DateSize);

            if (!isObjectNull(textSize)) {
                createTextStyles(textSize.value);
            }
            if (!isObjectNull(dateSize)) {
                createDateStyles(dateSize.value);
            }
        }

        const initializeState = async (applicationRights = []) => {
            setApplicationRights(applicationRights);
            // hvis man er offline, og datbasen er korrupt... gi beskjed om at man m� v�re online for � restore db
            const isDatabaseValid = await BaseIdbRepository.isDatabaseValidAsync();
            const applicationRightService = new ApplicationRightService(applicationRights);
            const canAccessPilotWeb = applicationRightService.canAccess([ApplicationRightSystemNames.NJORD_PILOT_WEB_LOGIN]);

            let newState = isDatabaseValid ? AppStates.Valid : AppStates.RestoreDatabase;

            if (canAccessPilotWeb && isDatabaseValid) {
                await initializeStyle();
                await setWhoAmIAsync();
            }

            setAppState(newState);
        }

        // application rights
        const initializeConfiguration = async () => {
            const response = await SourceApiRepository.getConfigAsync();
            if (response.ok) {
                const data = await response.json();
                setConfiguration(data);
                initCacheAsync(data);
            }
        }

        const initializeAIConnectionString = async () => {
            const response = await SourceApiRepository.getAiConnectionStringAsync();
            if (response.ok) {
                const data = await response.json();
                await AppInsightsService.initAsync(data, getLoggedInPersonId);
            } else {
                throw new Error(`An error occurred while fetching the Application Insights configuration.`);
            }
        }

        const initialize = async () => {

            if (!isLoggedIn()) {
                initializeState();
                return;
            }

            await initializeConfiguration();
            await initializeAIConnectionString();

            const response = await AuthApiRepository.getApplicationRightsAsync();
            if (response.ok) {
                const data = await response.json();
                initializeState(data);
            } else {
                initializeState();
            }
        }

        initializeTheme();
        initialize();
    }, [tokenParameter]);

    // Initialize function
    useEffect(() => {
        const isLoggedInTimer = setInterval(() => onIntervalAsync(), 300000); // Validate login every 5 minutes

        return () => {
            clearInterval(isLoggedInTimer);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleIndexedDBConnectionLost = (event) => {
            console.error('IndexedDB connection lost: ', event.detail);

            addWarningNotification(`Kunne ikke hente data. Oppfrisk siden eller start på nytt`);
        };

        window.addEventListener('indexeddb-connection-lost', handleIndexedDBConnectionLost);

        return () => {
            window.removeEventListener('indexeddb-connection-lost', handleIndexedDBConnectionLost);
        }
    }, []);
    
    return (
        (() => {
            switch (appState) {
                case AppStates.Valid:
                    return (
                        <AppRoutes />
                    );
                case AppStates.RestoreDatabase:
                    return (
                        <AppGrid>
                            <RestoreDatabase />
                        </AppGrid>
                    );
                default:
                    return (
                        <AppGrid>
                            <div className="k-d-grid k-grid-flow-row k-gap-4">
                                <div className="k-d-flex k-justify-content-center">
                                    {
                                        (darkmode) ?
                                            <img alt="Kystverket" src="/images/kyv-logo-neg.svg" width="340" fetchPriority="high" />
                                            :
                                            <img alt="Kystverket" src="/images/kyv-logo.svg" width="340" fetchPriority="high" />
                                    }
                                </div>
                                <div className="k-d-flex k-justify-content-center">
                                    <Loader type="converging-spinner" size="large" themeColor={darkmode ? "light" : "dark"} className="app-loader" />
                                </div>
                            </div>
                        </AppGrid>);
            }
        })()
    );

    async function onIntervalAsync() {

        if (!isNullOrEmpty(configuration)) {
            await initCacheAsync(configuration);
        }

        const token = getToken();
        if (isNullOrEmpty(token)) return;

        const loggedIn = isLoggedIn();
        if (loggedIn) return;

        window.location.reload();
    }
}

const AppGrid = ({ children }) => {
    return (
        <div
            className="k-d-grid k-w-full k-h-full k-justify-content-center k-align-items-center app-grid">
            {children}
        </div>
    )
}
